import React, { useState } from 'react';
import {
  Box,
  Typography,
  Input,
  Button,
  Sheet,
  Table,
  Checkbox,
  Select,
  Option,
} from '@mui/joy';
import { Ambulance } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../../utils/authFetch';

const livelli = ['Nessun accesso', 'Visualizzatore', 'Editor'];

const menuLabels = {
  2: 'Categorie',
  3: 'Mezzi',
  4: 'Utenti',
  5: 'Checklist',
};

export default function CreaOrganizzazioneCompleta() {
  const navigate = useNavigate();

  const [orgData, setOrgData] = useState({
    name: '',
    partita_iva: '',
    email_responsabile: '',
    indirizzo: '',
    licenza: null,
    vehicles: 0,
  });

  const [userData, setUserData] = useState({
    codice_fiscale: '',
    nome: '',
    username: '',
  });

  const [cfError, setCfError] = useState('');
  const [permessi, setPermessi] = useState({});
  const [verificato, setVerificato] = useState(false);
  const [utenteEsistente, setUtenteEsistente] = useState(false);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChangeOrg = (field, value) => {
    setOrgData((prev) => ({ ...prev, [field]: value }));
  };

  const handleChangeUser = (field, value) => {
    setUserData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePermessoChange = (menuId, tipo) => {
    setPermessi((prev) => ({
      ...prev,
      [menuId]: tipo,
    }));
  };

  const handleVerificaCF = async () => {
    const cfRegex = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i;
  
    if (!cfRegex.test(userData.codice_fiscale.trim())) {
      setCfError('Codice fiscale non valido');
      return;
    }
  
    setCfError('');
    setLoading(true);
    setVerificato(false);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/temp/user/cf/${userData.codice_fiscale}`,
        { credentials: 'include' }
      );
  
      if (!res.ok) throw new Error('Errore nella verifica del CF');
  
      const data = await res.json();
      if (data?.id) {
        setUserData((prev) => ({
          ...prev,
          nome: data.nome || '',
          username: data.username || '',
        }));
        setUtenteEsistente(true);
        setUserId(data.id);
      } else {
        setUtenteEsistente(false);
        setUserId(null);
      }
      setVerificato(true);
    } catch (err) {
      toast.error('Errore nella verifica codice fiscale');
    } finally {
      setLoading(false);
    }
  };
  

  const handleCreate = async () => {
    if (!verificato) {
      toast.error('Verifica prima il codice fiscale');
      return;
    }

    setLoading(true);
    try {
      // 1. Crea organizzazione con tutti i dettagli
      const resOrg = await authFetch(`${process.env.REACT_APP_SERVER_HOST}/org`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: orgData.name,
          licence: orgData.licenza,
          vehicles: orgData.vehicles,
          partita_iva: orgData.partita_iva,
          indirizzo: orgData.indirizzo,
          email_responsabile: orgData.email_responsabile,
        }),
      });

      if (!resOrg.ok) throw new Error('Errore creazione organizzazione');
      const org = await resOrg.json();
      const orgId = org.id;

      // 2. Crea o associa utente
      let finalUserId = userId;
      if (!utenteEsistente) {
        const resUser = await authFetch(`${process.env.REACT_APP_SERVER_HOST}/org/${orgId}/user`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(userData),
        });

        if (!resUser.ok) throw new Error('Errore creazione utente');
        const newUser = await resUser.json();
        finalUserId = newUser?.userId || newUser?.id;

        const resLink = await authFetch(
          `${process.env.REACT_APP_SERVER_HOST}/org/${orgId}/user/${finalUserId}/link`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: finalUserId }),
          }
        );

        if (!resLink.ok) throw new Error("Errore nell'associazione utente creato");
      } else {
        const resLink = await authFetch(
          `${process.env.REACT_APP_SERVER_HOST}/org/${orgId}/user/${finalUserId}/link`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: finalUserId }),
          }
        );

        if (!resLink.ok) throw new Error("Errore nell'associazione utente esistente");
      }

      // 3. Assegna permessi
      const permessiArray = Object.entries(permessi).map(([menu_id, tipo]) => ({
        menu_id: parseInt(menu_id, 10),
        tipo,
      }));

      const resPerm = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${orgId}/user/${finalUserId}/permessi`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ permessi_json: permessiArray }),
        }
      );

      if (!resPerm.ok) throw new Error('Errore assegnazione permessi');

      toast.success('Organizzazione e utente creati con successo');
      navigate('/org');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Errore nella creazione');
    } finally {
      setLoading(false);
    }
  };

  const orgFieldsFilled = orgData.name && orgData.partita_iva && orgData.email_responsabile && orgData.indirizzo;

  return (
    <Box>
      <Typography level="h3" mb={2}>Nuova Organizzazione</Typography>

      <Box display="flex" flexDirection={{ xs: 'column', lg: 'row' }} gap={4}>
        {/* Parte sinistra - Organizzazione */}
        <Sheet
          variant="outlined"
          sx={{
            flex: 2,
            p: 3,
            borderRadius: 'md',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minWidth: 300,
          }}
        >
          <Typography level="h4">Dati Organizzazione</Typography>

          <Input placeholder="Nome" value={orgData.name} onChange={(e) => handleChangeOrg('name', e.target.value)} />
          <Input placeholder="Partita IVA" value={orgData.partita_iva} onChange={(e) => handleChangeOrg('partita_iva', e.target.value)} />
          <Input placeholder="Email Responsabile" value={orgData.email_responsabile} onChange={(e) => handleChangeOrg('email_responsabile', e.target.value)} />
          <Input placeholder="Indirizzo" value={orgData.indirizzo} onChange={(e) => handleChangeOrg('indirizzo', e.target.value)} />

          {orgFieldsFilled && (
            <>
              <Select
                value={orgData.licenza}
                placeholder="Tipo di licenza"
                onChange={(_, value) => handleChangeOrg('licenza', value)}
              >
                <Option value={0}>Free</Option>
                <Option value={1}>Mensile</Option>
                <Option value={2}>Annuale</Option>
              </Select>

              <Input
                type="number"
                placeholder="Numero veicoli"
                value={orgData.vehicles}
                onChange={(e) => handleChangeOrg('vehicles', parseInt(e.target.value, 10) || 0)}
                startDecorator={<Ambulance size={16} />}
              />
            </>
          )}
        </Sheet>

        {/* Parte destra - Utente + Permessi */}
        <Sheet
          variant="outlined"
          sx={{
            flex: 2,
            p: 3,
            borderRadius: 'md',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minWidth: 300,
          }}
        >
          <Typography level="h4">Utente iniziale</Typography>

          <Box display="flex" gap={1}>
            <Input
              placeholder="Codice Fiscale"
              value={userData.codice_fiscale}
              onChange={(e) => handleChangeUser('codice_fiscale', e.target.value.toUpperCase())}
              sx={{ flex: 1 }}
              disabled={verificato}
            />
            <Button size="sm" onClick={handleVerificaCF} loading={loading} disabled={!userData.codice_fiscale}>
              Verifica
            </Button>
          </Box>
          {cfError && (
            <Typography level="body-sm" color="danger">
                {cfError}
            </Typography>
          )}

          <Input
            placeholder="Nome"
            value={userData.nome}
            onChange={(e) => handleChangeUser('nome', e.target.value)}
            disabled={!verificato || utenteEsistente}
          />
          <Input
            placeholder="Username"
            value={userData.username}
            onChange={(e) => handleChangeUser('username', e.target.value)}
            disabled={!verificato || utenteEsistente}
          />

          {verificato && (
            <>
              <Typography level="title-md" mt={2}>Permessi</Typography>
              <Table size="sm" borderAxis="both">
                <thead>
                  <tr>
                    <th>Sezione</th>
                    {livelli.map((liv, i) => (
                      <th key={i} style={{ textAlign: 'center' }}>{liv}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(menuLabels).map((menuId) => {
                    const id = parseInt(menuId, 10);
                    const tipo = permessi[id] ?? 0;
                    return (
                      <tr key={id}>
                        <td>{menuLabels[id]}</td>
                        {livelli.map((_, livello) => (
                          <td key={livello} style={{ textAlign: 'center' }}>
                            <Checkbox
                              checked={tipo === livello}
                              onChange={() => handlePermessoChange(id, livello)}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </>
          )}

          <Button
            onClick={handleCreate}
            loading={loading}
            disabled={!verificato || loading}
            sx={{ mt: 2 }}
          >
            Crea Organizzazione
          </Button>
        </Sheet>
      </Box>
    </Box>
  );
}
